// The map sheet: an 11x11 grid, wilderness side (A), with the five mountain
// spaces and six ruins spaces read off the mod's White Map tile (positions
// verified against the printed sheet's symmetry - see docs/).
//
// A grid is an array of 11 strings of 11 chars, one char per space:
//   '.' empty   'f' forest   'v' village   'a' farm   'w' water
//   'm' monster 'M' mountain (pre-printed, filled from the start)
// Ruins are NOT in the grid - they are fixed positions that stay "ruins"
// whether or not something is drawn over them (rules p9: only FILLED ruins
// spaces count as filled).
//
// Strings rather than nested arrays: the whole game state travels through
// Firebase RTDB, which silently drops empty arrays; strings dodge the
// problem the same way the sibling repos' JSON-string states do.

export const SIZE = 11;

// Row/col, 0-indexed from the top-left, from the White Map tile (rows B4, C9,
// F6, I3, J8 / ruins B6, C2, C10, I2, I10, J6 in the sheet's own A-K letters).
export const MOUNTAINS = [[1, 3], [2, 8], [5, 5], [8, 2], [9, 7]];
export const RUINS = [[1, 5], [2, 1], [2, 9], [8, 1], [8, 9], [9, 5]];

const TERRAIN_CHAR = {
  forest: 'f', village: 'v', farm: 'a', water: 'w', monster: 'm',
};
export const CHAR_TERRAIN = {
  f: 'forest', v: 'village', a: 'farm', w: 'water', m: 'monster', M: 'mountain',
};

export const emptyGrid = () => {
  const rows = Array.from({ length: SIZE }, () => '.'.repeat(SIZE));
  MOUNTAINS.forEach(([r, c]) => {
    rows[r] = `${rows[r].slice(0, c)}M${rows[r].slice(c + 1)}`;
  });
  return rows;
};

export const at = (grid, r, c) => grid[r][c];
export const inBounds = (r, c) => r >= 0 && r < SIZE && c >= 0 && c < SIZE;
export const isFilled = (grid, r, c) => grid[r][c] !== '.';
export const isRuin = (r, c) => RUINS.some(([rr, cc]) => rr === r && cc === c);

// Orthogonal neighbours only - "Spaces that are only at diagonals with each
// other are not adjacent" (rules p9).
export const neighbours = (r, c) => [[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]]
  .filter(([rr, cc]) => inBounds(rr, cc));

// ---- shape transforms ------------------------------------------------------

// Cells are [row, col] pairs. Normalised = translated so min row and min col
// are both 0, sorted - the canonical form used to compare shapes.
export const normalize = (shapeCells) => {
  const minR = Math.min(...shapeCells.map(([r]) => r));
  const minC = Math.min(...shapeCells.map(([, c]) => c));
  return shapeCells
    .map(([r, c]) => [r - minR, c - minC])
    .sort(([r1, c1], [r2, c2]) => r1 - r2 || c1 - c2);
};

const key = (shapeCells) => normalize(shapeCells).map(([r, c]) => `${r},${c}`).join(';');

// All distinct orientations under rotation and flip (rules p6) - up to 8,
// fewer for symmetric shapes.
export const orientationsOf = (shapeCells) => {
  const seen = new Map();
  let current = shapeCells;
  for (let flip = 0; flip < 2; flip += 1) {
    for (let rot = 0; rot < 4; rot += 1) {
      const norm = normalize(current);
      seen.set(key(norm), norm);
      current = current.map(([r, c]) => [c, -r]); // rotate 90
    }
    current = current.map(([r, c]) => [r, -c]); // mirror
  }
  return [...seen.values()];
};

// Same polyomino in ANY orientation - a placement arrives however the player
// rotated it, and it still has to match the card.
export const sameShape = (a, b) => {
  const target = key(b);
  return orientationsOf(a).some((o) => key(o) === target);
};

// ---- placement -------------------------------------------------------------

// Whether these absolute cells may be drawn on this grid: in bounds, no
// overlap with filled spaces (mountains count, rules p6), and - when a ruins
// card is active - covering at least one EMPTY ruins space (rules p7).
export const fitsAt = (grid, absCells, { needsRuin = false } = {}) => {
  let coversRuin = false;
  for (const [r, c] of absCells) {
    if (!inBounds(r, c) || isFilled(grid, r, c)) return false;
    if (isRuin(r, c)) coversRuin = true;
  }
  return needsRuin ? coversRuin : true;
};

// Every legal placement of one shape (all orientations x all offsets).
// Returns arrays of absolute [row, col] cells.
export const placementsOf = (grid, shapeCells, { needsRuin = false } = {}) => {
  const found = [];
  for (const orientation of orientationsOf(shapeCells)) {
    const maxR = Math.max(...orientation.map(([r]) => r));
    const maxC = Math.max(...orientation.map(([, c]) => c));
    for (let dr = 0; dr <= SIZE - 1 - maxR; dr += 1) {
      for (let dc = 0; dc <= SIZE - 1 - maxC; dc += 1) {
        const abs = orientation.map(([r, c]) => [r + dr, c + dc]);
        if (fitsAt(grid, abs, { needsRuin })) found.push(abs);
      }
    }
  }
  return found;
};

export const anyPlacement = (grid, shapes, { needsRuin = false } = {}) => shapes
  .some((shape) => placementsOf(grid, shape.cells, { needsRuin }).length > 0);

// Draw the cells. Returns [newGrid, coinsFromMountains] - filling the last
// empty neighbour of a mountain earns a coin (rules p6), checked here so no
// caller can forget it.
export const draw = (grid, absCells, terrain) => {
  const ch = TERRAIN_CHAR[terrain];
  const rows = grid.slice();
  absCells.forEach(([r, c]) => {
    rows[r] = `${rows[r].slice(0, c)}${ch}${rows[r].slice(c + 1)}`;
  });
  let mountainCoins = 0;
  MOUNTAINS.forEach(([mr, mc]) => {
    const wasSurrounded = neighbours(mr, mc).every(([r, c]) => isFilled(grid, r, c));
    const isSurrounded = neighbours(mr, mc).every(([r, c]) => rows[r][c] !== '.');
    if (!wasSurrounded && isSurrounded) mountainCoins += 1;
  });
  return [rows, mountainCoins];
};

// ---- analysis shared by scoring and the bot --------------------------------

// Connected groups of same-char cells ("cluster", rules p9). Returns arrays
// of [r, c].
export const clusters = (grid, ch) => {
  const seen = Array.from({ length: SIZE }, () => new Array(SIZE).fill(false));
  const out = [];
  for (let r = 0; r < SIZE; r += 1) {
    for (let c = 0; c < SIZE; c += 1) {
      if (grid[r][c] !== ch || seen[r][c]) continue; // eslint-disable-line no-continue
      const cluster = [];
      const stack = [[r, c]];
      seen[r][c] = true;
      while (stack.length) {
        const [cr, cc] = stack.pop();
        cluster.push([cr, cc]);
        neighbours(cr, cc).forEach(([nr, nc]) => {
          if (grid[nr][nc] === ch && !seen[nr][nc]) {
            seen[nr][nc] = true;
            stack.push([nr, nc]);
          }
        });
      }
      out.push(cluster);
    }
  }
  return out;
};
